import type { CityConfig } from "../city-template/types";

export const springfieldMoConfig: CityConfig = {
  city: "Springfield",
  state: "Missouri",
  stateAbbr: "MO",
  slug: "springfield-mo",
  route: "/cities/springfield-mo",
  title: "Emergency Passport Springfield, MO | Fast Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Springfield, MO — as fast as 24 hours. Fast passport renewal, same-day help, and lost passport replacement. A+ BBB rated, registered State Dept. courier serving Springfield and the Ozarks.",
  h1: "Emergency & Expedited Passport Services in Springfield, MO",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport, fast passport renewal, or same-day help? We help Springfield and Southwest Missouri travelers get their urgent passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Springfield%20Missouri%20downtown%20at%20golden%20hour%20with%20historic%20buildings%20and%20modern%20skyline%2C%20College%20Street%20view%20with%20warm%20sunset%20light%2C%20Ozark%20hills%20visible%20in%20background%2C%20warm%20amber%20and%20orange%20tones%2C%20professional%20cityscape%20photography%2C%20clean%20modern%20urban%20scene%20with%20small-town%20charm%2C%20no%20visible%20text%20or%20people%2C%20American%20Midwest%20architectural%20style&width=1800&height=700&seq=sf-mo-hero-001&orientation=landscape",
  heroImageAlt: "Springfield Missouri downtown at golden hour with historic buildings and Ozark hills",
  geoPosition: "37.2089;-93.2923",
  geoPlacename: "Springfield, Missouri",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 W Congress Pkwy, Chicago, IL 60605",
    distance: "Chicago is the nearest regional passport agency — approximately 8 hours from Springfield by car. Fast Passport Center eliminates the need for this drive with our registered courier service.",
  },
  nearbyCitiesZips: [
    { name: "Downtown Springfield", zips: "65801–65806" },
    { name: "Nixa", zips: "65714" },
    { name: "Ozark", zips: "65721" },
    { name: "Republic", zips: "65738" },
    { name: "Branson", zips: "65616" },
    { name: "Joplin", zips: "64801, 64804" },
    { name: "Lebanon", zips: "65536" },
    { name: "Marshfield", zips: "65706" },
    { name: "Bolivar", zips: "65613" },
    { name: "Willard", zips: "65781" },
    { name: "Rogersville", zips: "65742" },
    { name: "Strafford", zips: "65757" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Springfield departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Kansas City", route: "/cities/kansas-city" },
    { name: "Emergency passport help in St. Louis", route: "/cities/st-louis" },
    { name: "Urgent passport services in Columbia", route: "/cities/columbia-mo" },
    { name: "Expedited passport services in Missouri", route: "/states/missouri" },
    { name: "Fast passport renewal in Tulsa", route: "/states/oklahoma" },
    { name: "Passport courier services in Little Rock", route: "/states/arkansas" },
    { name: "Rush passport help in Memphis", route: "/cities/memphis" },
  ],
  fpcOfficeAddress: "101 W Congress Pkwy, 4th Floor, Chicago, IL 60605",
  fpcOfficeNote: "Fast Passport Center serves Springfield remotely via secure courier. We do not have a walk-in office in Springfield — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Springfield travelers.",
  fpcOfficeDistance: "Serving all of Greene County and the Ozarks region",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Springfield and the surrounding Ozarks region. From downtown Springfield to Nixa, from Ozark to Branson — we help Southwest Missouri travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Springfield neighborhoods and nearby cities including Nixa, Ozark, Republic, Branson, and Joplin. If you are in Greene County or the surrounding Ozark region, we have you covered.",
  howItWorksIntro: "Most Springfield travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait for standard mail. Our emergency and expedited passport process is designed for Southwest Missouri travelers who need fast, reliable results.",
  mistakesIntro: "With Springfield being a growing tourism and business hub, travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Springfield travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Springfield? Here are the most common ones we hear from Greene County travelers.",
  statePageRoute: "/states/missouri",
  statePageName: "Missouri",
  dmvWebsite: "https://dor.mo.gov/motor-vehicle",
  dmvLabel: "Missouri Motor Vehicle — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Springfield travelers with immediate departure needs — as fast as 24 hours for urgent international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Michael S.",
      location: "Nixa, MO",
      rating: 5,
      text: "My flight to Cancun was in 4 days and my passport had expired. Fast Passport Center got me a new one in 36 hours. Saved our family vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Jessica T.",
      location: "Ozark, MO",
      rating: 5,
      text: "I needed to renew my passport for a business trip to Germany. The process was smooth, they reviewed my documents, and I had my passport in 5 days.",
      service: "Passport Renewal",
    },
    {
      name: "Kevin R.",
      location: "Republic, MO",
      rating: 5,
      text: "Lost my passport before a mission trip to Guatemala. They walked me through the process and got me a replacement in 2 days. Amazing service.",
      service: "Lost Passport",
    },
    {
      name: "Lisa M.",
      location: "Springfield, MO",
      rating: 5,
      text: "As a frequent international traveler from the Ozarks, I need a passport service I can trust. Fast Passport Center delivers every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "sf-mo-1",
      name: "Springfield Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "501 W College St",
      city: "Springfield",
      state: "MO",
      zip: "65806",
      phone: "(417) 866-0163",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Springfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "sf-mo-2",
      name: "Nixa Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "701 N Main St",
      city: "Nixa",
      state: "MO",
      zip: "65714",
      phone: "(417) 725-2500",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Nixa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "sf-mo-3",
      name: "Ozark Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1340 W Jackson St",
      city: "Ozark",
      state: "MO",
      zip: "65721",
      phone: "(417) 581-2224",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Ozark",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Christian County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "sf-mo-4",
      name: "Springfield City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "840 Boonville Ave",
      city: "Springfield",
      state: "MO",
      zip: "65802",
      phone: "(417) 864-1651",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "0.2 miles from Springfield City Hall",
      website: "https://www.springfieldmo.gov/",
      websiteLabel: "Springfield City Clerk",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "sf-mo-5",
      name: "Branson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2001 State Highway 248",
      city: "Branson",
      state: "MO",
      zip: "65616",
      phone: "(417) 334-1161",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1 mile from Branson Landing",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Branson and Taney County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "sf-mo-6",
      name: "Greene County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "940 N Boonville Ave",
      city: "Springfield",
      state: "MO",
      zip: "65802",
      phone: "(417) 868-4000",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from downtown Springfield",
      website: "https://www.greenecountymo.gov/",
      websiteLabel: "Greene County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 8-hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Springfield photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Springfield acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Springfield applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Springfield families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Springfield travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Springfield." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Springfield address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Springfield?",
      a: "Fast Passport Center offers emergency passport processing in Springfield in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Springfield travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Springfield?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel within 72 hours. The nearest agency to Springfield is in Chicago, an 8-hour drive. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Springfield residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Springfield?",
      a: "Fast Passport Center offers fast passport renewal in Springfield with no office visit required. We serve all Springfield neighborhoods including Nixa, Ozark, Republic, and Branson. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Springfield?",
      a: "The fastest way to get an urgent passport in Springfield is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (3–5 weeks).",
    },
    {
      q: "How fast can I get a passport in Springfield?",
      a: "With Fast Passport Center's emergency courier service, Springfield travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Chicago Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Springfield?",
      a: "Popular options include the Springfield Main Post Office (65806), Nixa Post Office (65714), Ozark Post Office (65721), and Springfield City Clerk (65802). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in Branson or the southern Ozarks?",
      a: "We serve all of Southwest Missouri including Branson, Nixa, Ozark, and Republic. Our overnight shipping labels work from any UPS location, so your location in the Ozarks doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Springfield?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Springfield?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Springfield offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Springfield?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Springfield?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Springfield families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Springfield", address: "2825 S Glenstone Ave, Springfield, MO 65804", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Nixa", address: "701 N Main St, Nixa, MO 65714", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Springfield", address: "2020 S Glenstone Ave, Springfield, MO 65804", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Ozark", address: "1800 W Jackson St, Ozark, MO 65721", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Springfield", address: "2021 E Independence St, Springfield, MO 65804", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Branson", address: "1055 Branson Landing Blvd, Branson, MO 65616", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Republic", address: "1300 E US Highway 60, Republic, MO 65738", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Springfield", address: "3650 E Sunshine St, Springfield, MO 65809", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Springfield",
      address: "3040 E Sunshine St, Springfield, MO 65804",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Nixa",
      address: "701 N Main St, Nixa, MO 65714",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Branson",
      address: "2001 State Highway 248, Branson, MO 65616",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Ozark",
      address: "1340 W Jackson St, Ozark, MO 65721",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Christian County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SGF",
      name: "Springfield-Branson National Airport",
      distance: "8 miles from downtown Springfield",
      address: "2300 N Airport Blvd, Springfield, MO 65802",
      note: "The primary airport for Springfield and the Ozarks. Major hub for domestic flights with international connections to Cancun, Punta Cana, and other vacation destinations.",
      flightUrl: "https://www.springfieldbransonairport.com/",
      flightLabel: "SGF Airport Info",
      featured: true,
    },
    {
      code: "BKG",
      name: "Branson Airport",
      distance: "10 miles from Branson",
      address: "4000 Branson Airport Blvd, Hollister, MO 65672",
      note: "Smaller airport serving Branson and the Ozarks. Seasonal flights to major destinations.",
      flightUrl: "https://www.flybranson.com/",
      flightLabel: "Branson Airport",
      featured: false,
    },
  ],
};